import React, { useState, useEffect, useRef } from "react";
import "../horoscope/daily.css";
import APIURL from "../../common/Config";
import { useNavigate } from "react-router-dom";

const DailyHoroscope = () => {
  const [selectedSign, setSelectedSign] = useState("Aries");
  const [horoscopeData, setHoroscopeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeButton, setActiveButton] = useState("today");
  const navigate = useNavigate();

  const cacheRef = useRef(new Map());
  const abortControllerRef = useRef(null); 

  const signImages = {
    Aries: require("../../assets/Ariesss.webp"),
    Taurus: require("../../assets/Taurusss.webp"),
    Gemini: require("../../assets/Geminiii.webp"),
    Cancer: require("../../assets/Cancerrr.webp"),
    Leo: require("../../assets/Leooo.webp"),
    Virgo: require("../../assets/Virgooo.webp"),
    Libra: require("../../assets/Libraaa.webp"),
    Scorpio: require("../../assets/Scorpiooo.webp"),
    Sagittarius: require("../../assets/Sagittariusss.webp"),
    Capricorn: require("../../assets/Capricornnn.webp"),
    Aquarius: require("../../assets/Aqauriusss.webp"),
    Pisces: require("../../assets/Piscesss.webp"),
  };

  const formatDate = (dateNum) => {
    const dateStr = dateNum.toString();
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${day}-${month}-${year}`;
  };

 
  const fetchHoroscope = async (date) => {
    const dateStr = date.toISOString().slice(0, 10).replace(/-/g, "");
    setError(null);
    setLoading(true); 

   
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log("Aborted previous fetch request.");
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const localStorageKey = `horoscope_data_${dateStr}`;

   
    try {
      const cachedData = localStorage.getItem(localStorageKey);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        setHoroscopeData(parsedData);
        setLoading(false);
        return; 
      }
    } catch (e) {
      console.error("Failed to parse cached data from local storage, attempting fetch:", e);
      localStorage.removeItem(localStorageKey); 
    }

    
    if (cacheRef.current.has(dateStr)) {
      setHoroscopeData(cacheRef.current.get(dateStr));
      setLoading(false);
      return; 
    }

    
    let retries = 0;
    const MAX_DELAY_MS = 60 * 1000; 
    const INITIAL_DELAY_MS = 1000; 

    while (true) { 
      const minLoadingTimePromise = new Promise((resolve) => setTimeout(resolve, 500)); 
      let timeoutId;

      try {
        console.log(`Attempting to fetch horoscope for ${dateStr}. Attempt: ${retries + 1}`);

        
        timeoutId = setTimeout(() => {
          if (!signal.aborted) { 
            abortControllerRef.current.abort();
            console.warn(`Fetch request for ${dateStr} timed out after 20 seconds.`);
          }
        }, 20000); 

        const response = await fetch(
          `${APIURL}/horroscope/getallprediction?type=daily&startDate=${dateStr}&endDate=${dateStr}&language=english`,
          { signal: signal }
        );

        clearTimeout(timeoutId); 

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`HTTP error! Status: ${response.status}, Response: ${errorText} for ${dateStr}`);

        
          if (response.status >= 500 || response.status === 0) { 
            throw new Error(`Server or network error: Status ${response.status || 'unknown'}. Details: ${errorText}`);
          } else if (response.status === 404) {
            
            setError("Horoscope data not found for this date. Please try a different date.");
            setHoroscopeData([]);
            setLoading(false);
            return; 
          } else {
           
            throw new Error(`Client error: Status ${response.status}. Details: ${errorText}`);
          }
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setHoroscopeData(data);
          cacheRef.current.set(dateStr, data);
          localStorage.setItem(localStorageKey, JSON.stringify(data));
          await minLoadingTimePromise; 
          setLoading(false);
          console.log(`Loading state set to false for ${dateStr}.`);
          return; 
        } else {
          console.error("Unexpected API response format. Expected array, got:", data);
          throw new Error("Unexpected response format from API. Retrying..."); 
        }
      } catch (error) {
        
        clearTimeout(timeoutId); 
        if (signal.aborted) {
          console.log(`Fetch for ${dateStr} was aborted by user or timeout.`);
         
          if (error.name === 'AbortError') { 
            if (error.message.includes("timed out")) { 
              setError("Network request timed out. Please check your connection or try again.");
            } else {
              
              return;
            }
          }
        } else {
          console.error("Error fetching horoscope data:", error);
          setError(`Failed to load horoscope data: ${error.message}. Retrying...`);
        }

        retries++;
        const delay = Math.min(INITIAL_DELAY_MS * Math.pow(2, retries - 1), MAX_DELAY_MS);
        console.warn(`Attempt ${retries} failed for ${dateStr}. Retrying in ${delay / 1000} seconds.`);
        await new Promise(resolve => setTimeout(resolve, delay)); 
      } finally {
       
      }
    }
  };

  useEffect(() => {
    
    fetchHoroscope(selectedDate);

   
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null; 
      }
    };
  }, [selectedDate]);

  

  const handleDateChange = (days, button) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);

    if (newDate.toDateString() === selectedDate.toDateString()) return;

    setSelectedDate(newDate);
    setActiveButton(button);
  };

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const prediction = horoscopeData.find(
    (item) => item.sunShine && item.sunShine.toUpperCase() === selectedSign.toUpperCase()
  ) || { prediction: "Horoscope data not available for this sign or date.", startDate: null };


  const displayDate =
    prediction && prediction.startDate
      ? formatDate(prediction.startDate)
      : "N/A";

  const getTitle = () => {
    const today = new Date().toDateString();
    const selected = selectedDate.toDateString();
    const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toDateString();
    const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1)).toDateString();

    if (selected === today) return "Your horoscope for today";
    if (selected === yesterday) return "Missed a day? Catch up here";
    if (selected === tomorrow) return "See what's ahead for you";
    return `Horoscope for ${selectedSign} on ${selectedDate.toLocaleDateString()}`;
  };

  const handleYearlyClick = () => {
    navigate(`/yearly-horoscope`);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading your horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
       
        {!error.includes("not found") && (
          <button onClick={() => fetchHoroscope(selectedDate)}>Retry Now</button>
        )}
      </div>
    );
  }

  const title = getTitle();

  return (
    <div className="horoscope-container-fluid">
      <div className="horoscope-container">
        <div className="horoscope-content">
          <h2 className="horoscope-title">{title}</h2>
          <div className="horoscope-image">
            <img src={signImages[selectedSign]} alt={`${selectedSign} Icon`} />
          </div>
          <div className="horoscope-date">{displayDate}</div>
          <p className="horoscope-text">
            {prediction.prediction.replace(/\s+/g, " ").trim()}
          </p>
        </div>

        <div className="horoscope-sidebar">
          <label className="dropdown-label">Choose a Different Sign</label>
          <select
            className="sign-dropdown"
            onChange={(e) => setSelectedSign(e.target.value)}
            value={selectedSign}
          >
            {Object.keys(signImages).map((sign) => (
              <option key={sign} value={sign}>
                {sign}
              </option>
            ))}
          </select>

          <button className="discover-button">Discover More Horoscopes</button>

          <div className="navigation-links">
            <ul>
              <li>
                <button
                  className={`horobtn ${activeButton === "yesterday" ? "active" : ""}`}
                  onClick={() => handleDateChange(-1, "yesterday")}
                >
                  Yesterday
                </button>
              </li>
              <li>
                <button
                  className={`horobtn ${activeButton === "today" ? "active" : ""}`}
                  onClick={() => {
                    const today = new Date();
                    if (today.toDateString() === selectedDate.toDateString()) return;
                    setSelectedDate(today);
                    setActiveButton("today");
                  }}
                >
                  Today
                </button>
              </li>
              <li>
                <button
                  className={`horobtn ${activeButton === "tomorrow" ? "active" : ""}`}
                  onClick={() => handleDateChange(1, "tomorrow")}
                >
                  Tomorrow
                </button>
              </li>
              <li>
                <button className="horobtn" onClick={handleYearlyClick}>
                  Yearly
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="faq-section">
        <div className="container-item">
          <h3>Frequently Asked Questions (FAQs) – Daily Horoscope</h3>
          <div className="fitem">
            {[
              {
                question: "1. What is a daily horoscope?",
                answer:
                  "A daily horoscope is an astrological forecast based on planetary movements, offering insights into various aspects of life such as career, relationships, health, and finances.",
              },
              {
                question: "2. How reliable are daily horoscopes?",
                answer:
                  "Daily horoscopes provide general guidance based on sun signs and celestial alignments.",
              },
              {
                question: "3. Can a horoscope predict my future?",
                answer:
                  "Horoscopes provide insights into potential trends and energies based on your sign. They are not absolute predictions.",
              },
              {
                question: "4. Why do different sources offer varying horoscopes?",
                answer:
                  "Different astrologers use different interpretations and methods, which can result in variation.",
              },
              {
                question: "5. Should I read my horoscope every day?",
                answer:
                  "It's a personal choice. Some enjoy daily insights, others prefer weekly or monthly readings.",
              },
              {
                question: "6. How is my daily horoscope determined?",
                answer:
                  "Your horoscope is based on the position of the planets in relation to your sun sign.",
              },
              {
                question: "7. Can horoscopes assist with decision-making?",
                answer:
                  "They can guide you through trends but shouldn’t be the sole basis for decisions.",
              },
              {
                question: "8. Should I read my sun sign or moon sign?",
                answer:
                  "Daily horoscopes are typically based on your sun sign.",
              },
              {
                question: "9. Can horoscopes influence my mood?",
                answer:
                  "Yes, they often uplift or motivate people with positive insights.",
              },
              {
                question: "10. Where can I find my daily horoscope on AstroBhagya?",
                answer:
                  "On the Daily Horoscope page of AstroBhagya.",
              },
            ].map((item, index) => (
              <div className="faq-item" key={index}>
                <div
                  className={`faq-question ${activeIndex === index ? "active" : ""}`}
                  onClick={() => toggleAnswer(index)}
                >
                  {item.question}
                </div>
                <div className={`faq-answer ${activeIndex === index ? "show" : ""}`}>
                  {item.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyHoroscope;

